import React from 'react';
import { Card, CardContent } from '../ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import { BarChart3, PieChart, TrendingUp } from 'lucide-react';
import { formatMoney } from './constants';

export default function ComptabiliteCharts({ comptaStats, filtered, getAmount, isPaid }) {
  return (
    <>
              {/* Section Graphiques Interactifs */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Graphique Évolution des revenus mensuels */}
                <Card className="shadow-lg border-0 overflow-hidden">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
                    <h4 className="text-white font-semibold flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Évolution des Revenus Mensuels
                    </h4>
                  </div>
                  <CardContent className="pt-6">
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart
                        data={(() => {
                          // Générer données mensuelles à partir des souscriptions
                          const monthlyData = {};
                          const now = new Date();
                          // Initialiser les 6 derniers mois
                          for (let i = 5; i >= 0; i--) {
                            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                            const key = d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
                            monthlyData[key] = { mois: key, encaisse: 0, facture: 0 };
                          }
                          // Agréger les données des souscriptions
                          filtered.forEach(doc => {
                            const date = new Date(doc.date_souscription || doc.created_at);
                            const key = date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
                            if (monthlyData[key]) {
                              monthlyData[key].facture += getAmount(doc) || 0;
                              if (isPaid(doc)) {
                                monthlyData[key].encaisse += getAmount(doc) || 0;
                              }
                            }
                          });
                          return Object.values(monthlyData);
                        })()}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorEncaisse" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorFacture" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="mois" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                        <Tooltip 
                          formatter={(value) => formatMoney(value)}
                          labelStyle={{ color: '#374151', fontWeight: 'bold' }}
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="facture" 
                          stroke="#6366f1" 
                          fillOpacity={1} 
                          fill="url(#colorFacture)" 
                          name="Facturé"
                          strokeWidth={2}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="encaisse" 
                          stroke="#10b981" 
                          fillOpacity={1} 
                          fill="url(#colorEncaisse)" 
                          name="Encaissé"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                    <div className="flex justify-center gap-6 mt-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                        <span className="text-sm text-gray-600">Facturé</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                        <span className="text-sm text-gray-600">Encaissé</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Graphique Répartition par mode de paiement */}
                <Card className="shadow-lg border-0 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4">
                    <h4 className="text-white font-semibold flex items-center gap-2">
                      <PieChart className="h-5 w-5" />
                      Répartition par Mode de Paiement
                    </h4>
                  </div>
                  <CardContent className="pt-6">
                    <ResponsiveContainer width="100%" height={280}>
                      <RechartsPieChart>
                        <Pie
                          data={(() => {
                            const modes = comptaStats?.paiements_par_mode || {};
                            const colors = {
                              virement: '#6366f1',
                              sepa: '#3b82f6',
                              carte: '#10b981',
                              cheque: '#f59e0b',
                              especes: '#8b5cf6'
                            };
                            return Object.entries(modes).map(([mode, data]) => ({
                              name: mode.charAt(0).toUpperCase() + mode.slice(1),
                              value: data.montant || 0,
                              count: data.count || 0,
                              color: colors[mode] || '#94a3b8'
                            })).filter(item => item.value > 0);
                          })()}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                        >
                          {(() => {
                            const modes = comptaStats?.paiements_par_mode || {};
                            const colors = {
                              virement: '#6366f1',
                              sepa: '#3b82f6',
                              carte: '#10b981',
                              cheque: '#f59e0b',
                              especes: '#8b5cf6'
                            };
                            return Object.entries(modes).map(([mode], index) => (
                              <Cell key={`cell-${index}`} fill={colors[mode] || '#94a3b8'} />
                            ));
                          })()}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name, props) => [
                            `${formatMoney(value)} (${props.payload.count} paiements)`,
                            props.payload.name
                          ]}
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                          }}
                        />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Graphique barres - Comparaison Souscriptions */}
              <Card className="shadow-lg border-0 overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                  <h4 className="text-white font-semibold flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Souscriptions par Statut de Paiement
                  </h4>
                </div>
                <CardContent className="pt-6">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={[
                        { 
                          name: 'Acquittées', 
                          count: comptaStats?.souscriptions?.acquittees || 0,
                          montant: comptaStats?.montants?.encaisse || 0,
                          fill: '#10b981'
                        },
                        { 
                          name: 'En attente', 
                          count: comptaStats?.souscriptions?.en_attente || 0,
                          montant: (comptaStats?.montants?.total || 0) - (comptaStats?.montants?.encaisse || 0),
                          fill: '#f59e0b'
                        },
                        { 
                          name: 'Avec acompte', 
                          count: comptaStats?.acomptes?.recus || 0,
                          montant: comptaStats?.acomptes?.montant_recus || 0,
                          fill: '#6366f1'
                        },
                        { 
                          name: 'Programmés', 
                          count: comptaStats?.acomptes?.programmes || 0,
                          montant: comptaStats?.acomptes?.montant_programmes || 0,
                          fill: '#3b82f6'
                        }
                      ]}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                      <YAxis yAxisId="left" orientation="left" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'Montant') return formatMoney(value);
                          return value;
                        }}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="count" name="Nombre" radius={[4, 4, 0, 0]}>
                        {[
                          <Cell key="cell-0" fill="#10b981" />,
                          <Cell key="cell-1" fill="#f59e0b" />,
                          <Cell key="cell-2" fill="#6366f1" />,
                          <Cell key="cell-3" fill="#3b82f6" />
                        ]}
                      </Bar>
                      <Bar yAxisId="right" dataKey="montant" name="Montant" radius={[4, 4, 0, 0]} opacity={0.7}>
                        {[
                          <Cell key="cell-0" fill="#10b981" />,
                          <Cell key="cell-1" fill="#f59e0b" />,
                          <Cell key="cell-2" fill="#6366f1" />,
                          <Cell key="cell-3" fill="#3b82f6" />
                        ]}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
    </>
  );
}
